import type { AccessMatrixRow } from '@/entities/admin/model/adminSchema';
import type { UserRole } from '@/entities/user/model/userSchema';

export type AccessLevel = 'full' | 'view' | 'none';

const roleToColumn: Record<UserRole, keyof Omit<AccessMatrixRow, 'module'>> = {
  admin: 'admin',
  ot_specialist: 'ot_specialist',
  department_head: 'department_head',
  master: 'master',
  viewer: 'viewer',
  employee: 'employee',
};

export function getModuleAccess(
  matrix: AccessMatrixRow[] | undefined,
  role: UserRole | undefined,
  module: string,
): AccessLevel {
  if (!matrix?.length || !role) return 'none';
  const row = matrix.find((r) => r.module === module);
  if (!row) return 'none';
  return row[roleToColumn[role]];
}

export function canView(matrix: AccessMatrixRow[] | undefined, role: UserRole | undefined, module: string): boolean {
  return getModuleAccess(matrix, role, module) !== 'none';
}

export function canEdit(matrix: AccessMatrixRow[] | undefined, role: UserRole | undefined, module: string): boolean {
  return getModuleAccess(matrix, role, module) === 'full';
}

export function isReadOnly(matrix: AccessMatrixRow[] | undefined, role: UserRole | undefined, module: string): boolean {
  return getModuleAccess(matrix, role, module) === 'view';
}

/**
 * Route-key → access-matrix module name.
 * Used by sidebar filtering and route guards.
 */
export const ROUTE_MODULE_MAP: Record<string, string> = {
  '/': 'Дашборд KPI и контрольных показателей',
  '/employees': 'Сотрудники и карточка сотрудника',
  '/medical': 'Медосмотры',
  '/training': 'Обучение и инструктажи',
  '/sout': 'СОУТ и ОПР',
  '/risks': 'СОУТ и ОПР',
  '/siz': 'Нормы СИЗ',
  '/documents': 'НПА и документы',
  '/reports': 'Отчётность',
  '/admin': 'Администрирование и настройки',
};

/**
 * For routes whose visibility depends on multiple matrix rows
 * (e.g. /admin is accessible if user has access to either
 * «Администрирование и настройки» or «Журнал безопасности»).
 */
export const ROUTE_ALT_MODULES: Record<string, string[]> = {
  '/admin': ['Администрирование и настройки', 'Журнал безопасности'],
};

export function canAccessRoute(
  matrix: AccessMatrixRow[] | undefined,
  role: UserRole | undefined,
  routeKey: string,
): boolean {
  const altModules = ROUTE_ALT_MODULES[routeKey];
  if (altModules) {
    return altModules.some((mod) => canView(matrix, role, mod));
  }
  const module = ROUTE_MODULE_MAP[routeKey];
  if (!module) return true;
  return canView(matrix, role, module);
}
